// CU-86d45b38j — billing inside the customer profile over finance's subject-bound contract only; nothing here reads a
// user id or a Stripe customer id, so the generic UserProfileBilling (which sends both) is deliberately not reused.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Col, Modal, Result, Row, Space, Statistic, Typography } from "antd";
import { CreditCardOutlined, DollarOutlined, FileTextOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { InlineLoading, SectionCard } from "../../components/shared";
import PaymentMethodCardForm from "../../components/finance/PaymentMethodCardForm";
import PaymentMethodListView from "../../components/finance/PaymentMethodListView";
import { classifyCustomerAccessError } from "../../api/customer/customerAccess";
import {
	attachCustomerPaymentMethod,
	detachCustomerPaymentMethod,
	fetchCustomerBilling,
	fetchCustomerPaymentMethods,
	setDefaultCustomerPaymentMethod,
	setupCustomerBilling,
} from "../../api/customer/customerBillingApi";
import type { CustomerBillingState } from "../../api/customer/customerBillingApi";
import { formatCurrency } from "../../api/finance/FinanceApi";
import { useNotifications } from "../../context/NotificationContext";
import { customerBillingCacheScope, customerBillingKeys } from "./customerBillingCache";

const { Text } = Typography;

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
	<div>
		<Text type="secondary" style={{ fontSize: 11 }}>
			{label}
		</Text>
		<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{value}</Text>
	</div>
);

const CustomerProfileBilling: React.FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotifications();
	const [addOpen, setAddOpen] = useState(false);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const scope = customerBillingCacheScope();
	const stateKey = customerBillingKeys.state(scope);
	const methodsKey = customerBillingKeys.paymentMethods(scope);

	const billing = useQuery({
		queryKey: stateKey,
		queryFn: fetchCustomerBilling,
		// CU-86d45b38j: no automatic retry; a 403 is a decision and each outcome renders its own action.
		retry: false,
	});
	const ready = !!billing.data && billing.data.available && !billing.data.setupRequired;
	const methods = useQuery({
		queryKey: methodsKey,
		queryFn: fetchCustomerPaymentMethods,
		enabled: ready,
		retry: false,
	});

	const refreshMethods = () => queryClient.invalidateQueries({ queryKey: methodsKey });

	const setup = useMutation({
		mutationFn: setupCustomerBilling,
		onSuccess: (state: CustomerBillingState) => {
			queryClient.setQueryData(stateKey, state);
			showSuccess("Billing account ready");
		},
		onError: () => showError("Failed to set up billing account"),
	});

	const setDefault = useMutation({
		mutationFn: setDefaultCustomerPaymentMethod,
		onMutate: (paymentMethodId: string) => setActionLoading(paymentMethodId),
		onSuccess: () => {
			showSuccess("Default payment method updated");
			return refreshMethods();
		},
		onError: () => showError("Failed to update default payment method"),
		onSettled: () => setActionLoading(null),
	});

	const remove = useMutation({
		mutationFn: detachCustomerPaymentMethod,
		onMutate: (paymentMethodId: string) => setActionLoading(paymentMethodId),
		onSuccess: () => {
			showSuccess("Payment method removed");
			return refreshMethods();
		},
		onError: () => showError("Failed to remove payment method"),
		onSettled: () => setActionLoading(null),
	});

	const renderBody = () => {
		if (billing.isLoading) return <InlineLoading minHeight={200} message="Loading billing information…" />;

		if (billing.isError) {
			const outcome = classifyCustomerAccessError(billing.error);

			if (outcome === "session_refresh_required") {
				return (
					<div data-testid="billing-refresh-required">
						<Result
							status="warning"
							title="Please sign in again"
							subTitle="Your session was started before customer access was enabled, or it has expired. Sign in again to see your billing."
							extra={
								<Button type="primary" onClick={() => navigate("/logout")} data-testid="billing-sign-in-again">
									Sign in again
								</Button>
							}
						/>
					</div>
				);
			}

			if (outcome === "forbidden") {
				return (
					<div data-testid="billing-forbidden">
						<Result
							status="403"
							title="Customer billing is not available for this account"
							subTitle="This account is not part of the customer directory. If you believe it should be, please contact the clinic."
						/>
					</div>
				);
			}

			return (
				<div data-testid="billing-error">
					<Result
						status="error"
						title="We couldn't load your billing information"
						subTitle="Something went wrong on our side. Please try again."
						extra={
							<Button onClick={() => billing.refetch()} loading={billing.isFetching} data-testid="billing-retry">
								Try again
							</Button>
						}
					/>
				</div>
			);
		}

		const state = billing.data as CustomerBillingState;

		if (!state.available) {
			return (
				<div data-testid="billing-unavailable">
					<Alert
						message="Billing Not Available"
						description="Your organization has not enabled billing functionality. Please contact your administrator for more information."
						type="info"
						showIcon
						icon={<CreditCardOutlined />}
					/>
				</div>
			);
		}

		if (state.setupRequired) {
			return (
				<SectionCard title="Billing">
					<Space direction="vertical" size="middle" data-testid="billing-setup-required">
						<Text>Set up billing to save a payment method with the clinic.</Text>
						<Button type="primary" icon={<CreditCardOutlined />} loading={setup.isPending} onClick={() => setup.mutate()} data-testid="billing-setup">
							Set up billing
						</Button>
					</Space>
				</SectionCard>
			);
		}

		const defaultPaymentMethodId = methods.data?.find((method) => method.isDefault)?.id ?? null;

		return (
			<>
				{state.overview && (
					<SectionCard title="Billing Overview">
						<Row gutter={[16, 16]} data-testid="billing-overview">
							<Col xs={24} md={8}>
								<Statistic
									title="Total Paid"
									value={state.overview.payments.totalAmount}
									precision={2}
									prefix={<DollarOutlined style={{ color: "var(--mg-success)" }} />}
									valueStyle={{ color: "var(--mg-success)" }}
									formatter={(value) => <span data-testid="billing-total-paid">{formatCurrency(Number(value))}</span>}
								/>
							</Col>
							<Col xs={24} md={8}>
								<Statistic
									title="Pending Payments"
									value={state.overview.payments.pending}
									prefix={<CreditCardOutlined style={{ color: "var(--mg-warning)" }} />}
									valueStyle={{ color: "var(--mg-warning)" }}
									formatter={(value) => <span data-testid="billing-pending-payments">{String(value)}</span>}
									suffix={
										<Text type="secondary" style={{ fontSize: 14 }}>
											payments
										</Text>
									}
								/>
							</Col>
							<Col xs={24} md={8}>
								<Statistic
									title="Outstanding Invoices"
									value={state.overview.invoices.pending + state.overview.invoices.overdue}
									prefix={<FileTextOutlined style={{ color: state.overview.invoices.overdue > 0 ? "var(--mg-danger)" : "var(--mg-primary)" }} />}
									valueStyle={{ color: state.overview.invoices.overdue > 0 ? "var(--mg-danger)" : "var(--mg-primary)" }}
									formatter={(value) => <span data-testid="billing-outstanding-invoices">{String(value)}</span>}
									suffix={
										<Text type="secondary" style={{ fontSize: 14 }}>
											invoices
										</Text>
									}
								/>
							</Col>
						</Row>
					</SectionCard>
				)}

				<SectionCard title="Billing Information">
					<Space direction="vertical" size="middle" data-testid="billing-account">
						{state.account?.email && <Field label="Email" value={state.account.email} />}
						{state.account?.name && <Field label="Name" value={state.account.name} />}
						{state.account?.phone && <Field label="Phone" value={state.account.phone} />}
					</Space>
				</SectionCard>

				<SectionCard title="Payment Methods" noPadding>
					<div style={{ padding: 20 }}>
						{methods.isError ? (
							<Alert
								type="error"
								showIcon
								message="We couldn't load your payment methods"
								action={
									<Button size="small" onClick={() => methods.refetch()} loading={methods.isFetching} data-testid="payment-methods-retry">
										Try again
									</Button>
								}
								data-testid="payment-methods-error"
							/>
						) : (
							<PaymentMethodListView
								paymentMethods={methods.data ?? []}
								loading={methods.isLoading}
								actionLoading={actionLoading}
								defaultPaymentMethodId={defaultPaymentMethodId}
								onAddNew={() => setAddOpen(true)}
								onSetDefault={(paymentMethodId) => setDefault.mutate(paymentMethodId)}
								onRemove={(paymentMethodId) => remove.mutate(paymentMethodId)}
							/>
						)}
					</div>
				</SectionCard>

				<Modal title="Add Payment Method" open={addOpen} onCancel={() => setAddOpen(false)} footer={null} width={600} destroyOnClose>
					<PaymentMethodCardForm
						onAttach={attachCustomerPaymentMethod}
						onSuccess={() => {
							setAddOpen(false);
							void refreshMethods();
						}}
						onCancel={() => setAddOpen(false)}
					/>
				</Modal>
			</>
		);
	};

	return (
		<div data-testid="customer-profile-billing" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{renderBody()}
		</div>
	);
};

export default CustomerProfileBilling;
